import { collection, getDocs, doc, getDoc, setDoc, increment } from 'firebase/firestore';
import { db } from '../firebase/config';

// Function to get real statistics for the home page
export const getRealStats = async () => {
  try {
    let userCount = 0;
    let totalTestCount = 0;

    // First, try to get stats from publicStats collection (most reliable)
    try {
      const publicStatsDoc = await getDoc(doc(db, 'publicStats', 'homePageStats'));
      if (publicStatsDoc.exists()) {
        const stats = publicStatsDoc.data();
        userCount = stats.users || stats.userCount || 0;
        totalTestCount = stats.totalTestCount || 0;
        
        // If we have cached stats, use them
        if (userCount > 0 || totalTestCount > 0) {
          const formatNumber = (num, isUserCount = false) => {
            // Apply 3141+ formula to user count
            if (isUserCount && num > 0) {
              num = 3141 + num;
            }
            
            return `${num}+`;
          };

          return {
            success: true,
            stats: {
              students: userCount > 0 ? formatNumber(userCount, true) : '1M+',
              mockTests: totalTestCount > 0 ? formatNumber(totalTestCount) : '10K+',
              successRate: '98%',
              support: '24/7'
            }
          };
        }
      }
    } catch (error) {
      // Fallback to direct queries if publicStats fails
    }

    // Fallback: Get user count directly from users collection
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      userCount = usersSnapshot.size;
    } catch (error) {
      userCount = 0;
    }

    // Fallback: Get total tests count from both collections
    try {
      const [mockTestsSnapshot, testsSnapshot] = await Promise.all([
        getDocs(collection(db, 'mockTests')),
        getDocs(collection(db, 'tests'))
      ]);
      
      const mockTestCount = mockTestsSnapshot.size;
      const testCount = testsSnapshot.size;
      totalTestCount = mockTestCount + testCount;
    } catch (error) {
      totalTestCount = 0;
    }

    // Format numbers for display
    const formatNumber = (num, isUserCount = false) => {
      // Apply 3141+ formula to user count
      if (isUserCount && num > 0) {
        num = 3141 + num;
      }
      
      return `${num}+`;
    };

    // Use actual counts if available, otherwise use fallback
    return {
      success: true,
      stats: {
        students: userCount > 0 ? formatNumber(userCount, true) : '1M+',
        mockTests: totalTestCount > 0 ? formatNumber(totalTestCount) : '10K+',
        successRate: '98%',
        support: '24/7'
      }
    };
  } catch (error) {
    return {
      success: false,
      error,
      stats: {
        students: '1M+', // Fallback values
        mockTests: '10K+',
        successRate: '98%',
        support: '24/7'
      }
    };
  }
};

// Function to increment test count when a new test is created
export const incrementTestCount = async () => {
  try {
    const statsRef = doc(db, 'publicStats', 'homePageStats');
    
    // Check if document exists
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      // Increment existing count
      await setDoc(statsRef, {
        totalTestCount: increment(1),
        lastUpdated: new Date()
      }, { merge: true });
    } else {
      // Create new document with count of 1
      await setDoc(statsRef, {
        totalTestCount: 1,
        users: 0,
        userCount: 0,
        lastUpdated: new Date()
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error incrementing test count:', error);
    return { success: false, error: error.message };
  }
};

// Function to decrement test count when a test is deleted
export const decrementTestCount = async () => {
  try {
    const statsRef = doc(db, 'publicStats', 'homePageStats');
    
    // Check if document exists
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      const currentCount = statsDoc.data().totalTestCount || 0;
      
      // Only decrement if count is greater than 0
      if (currentCount > 0) {
        await setDoc(statsRef, {
          totalTestCount: increment(-1),
          lastUpdated: new Date()
        }, { merge: true });
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error decrementing test count:', error);
    return { success: false, error: error.message };
  }
};

// Function to manually refresh all stats (counts from collections)
export const refreshAllStats = async () => {
  try {
    // Count users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const userCount = usersSnapshot.size;
    
    // Count tests
    const [mockTestsSnapshot, testsSnapshot] = await Promise.all([
      getDocs(collection(db, 'mockTests')),
      getDocs(collection(db, 'tests'))
    ]);
    
    const totalTestCount = mockTestsSnapshot.size + testsSnapshot.size;
    
    // Update publicStats
    const statsRef = doc(db, 'publicStats', 'homePageStats');
    await setDoc(statsRef, {
      users: userCount,
      userCount: userCount,
      totalTestCount: totalTestCount,
      lastUpdated: new Date()
    });
    
    return {
      success: true,
      stats: {
        users: userCount,
        totalTestCount: totalTestCount
      }
    };
  } catch (error) {
    console.error('Error refreshing stats:', error);
    return { success: false, error: error.message };
  }
};
